import { t } from "@/i18n-js/instance";
import { useRoom } from "@circle-react/components/LiveStreams/Room/hooks";
import { useRoomType } from "@circle-react/components/LiveStreams/Room/hooks";
import { useStreamHoursAddon } from "@circle-react/components/LiveStreams/hooks";
import { useLiveStreamHoursAvailable } from "@circle-react/components/LiveStreams/hooks";
import { usePunditUserContext } from "@circle-react/contexts";
import {
  canManageRoom,
  roomHostCapacityReached,
} from "@circle-react/helpers/liveStreamRoomHelpers";
import { roomTypeData } from "@circle-react/helpers/liveStreamRoomHelpers";
import { useToast } from "@circle-react-uikit/ToastV2";

export const useLivePrejoinAddonsPurchaseFlow = (joinAsHost: () => void) => {
  const { currentCommunityMember } = usePunditUserContext();
  const isEnabled = !!currentCommunityMember?.is_admin;
  const room = useRoom();
  const { roomType } = useRoomType();

  const {
    streamingHoursAvailable: isStreamingHoursAvailable,
    isLoading: isLoadingUsage,
    streamingTotalPlanHours,
  } = useLiveStreamHoursAvailable({ enabled: isEnabled });

  const shouldPurchaseStreamingAddons = isEnabled && !isStreamingHoursAvailable;

  const hasPurchaseRecordingHoursButton = shouldPurchaseStreamingAddons;

  const isAddonPurchaseNotAllowed =
    !canManageRoom(room) && hasPurchaseRecordingHoursButton;

  const shouldAllowAddonPurchase =
    canManageRoom(room) && hasPurchaseRecordingHoursButton;

  const { success } = useToast();

  const joinAndToast = () => {
    joinAsHost();
    success(t("settings.billing.add_ons.changes_succeeded"));
  };

  const {
    purchaseAsync: purchaseStreamingAddon,
    unitSize: streamingAddonUnitSize,
    currentQuantity: streamHoursAddonQuantity,
  } = useStreamHoursAddon(() => joinAndToast(), { enabled: isEnabled });

  const purchaseStreamingHours = async () => {
    if (!streamingAddonUnitSize) return;

    await purchaseStreamingAddon(streamingAddonUnitSize);
  };

  const boxMessage = () => {
    if (isAddonPurchaseNotAllowed)
      return t(
        `live_streams.pre_join.${roomTypeData(room)}.non_host_upgrade_message`,
      );
    if (shouldPurchaseStreamingAddons)
      return t("live_streams.pre_join.streaming_limit_exceeded", {
        hours: streamingTotalPlanHours + Number(streamHoursAddonQuantity || 0),
      });
    if (roomHostCapacityReached(room))
      return t(
        `live_streams.pre_join.${roomTypeData(
          room,
        )}.host_capacity_reached_message`,
      );
    return "";
  };

  const onClickHandler = () => {
    if (shouldPurchaseStreamingAddons) return purchaseStreamingHours();

    return joinAsHost();
  };

  const liveButtonLabel = () => {
    if (shouldAllowAddonPurchase)
      return t(`live_streams.pre_join.${roomType}.upgrade_and_continue`);

    return t("live_streams.pre_join.conference.room_button_label");
  };

  const backstageButtonLabel = () => {
    if (shouldAllowAddonPurchase)
      return t(`live_streams.pre_join.${roomType}.upgrade_and_continue`);

    return t(`live_streams.pre_join.${roomType}.enter_backstage_button_label`);
  };

  return {
    addonPurchaseNotAllowed: isAddonPurchaseNotAllowed,
    isLoadingUsage,
    onClickHandler,
    liveButtonLabel,
    backstageButtonLabel,
    boxMessage,
  };
};
